import { computed, type Ref } from 'vue'
import { useInfiniteQuery, useQuery } from '@tanstack/vue-query'
import { useFavouritesStore } from '@/stores/favourites'
import { useApiClient } from '~/composables/api/api'
import {
  PORTAL_SEARCH_FAVOURITE_MAPPINGS,
  createPortalSearchFacetsRequest,
  createPortalSearchResultsRequest,
  mapPortalSearchResult,
  type PortalSearchCriteria,
  type PortalSearchEntityType,
  type PortalSearchFacetsResponse,
  type PortalSearchResult,
  type PortalSearchResponse,
} from '~/utils/portalGlobalSearch'

const PAGE_SIZE = 8
const SEARCH_CACHE_OPTIONS = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
  throwOnError: false,
} as const

type UsePortalGlobalSearchOptions = {
  searchTerm: Ref<string>
  language: Ref<string>
  selectedTypes: Ref<PortalSearchEntityType[]>
  favouritesOnly: Ref<boolean>
  enabled: Ref<boolean>
}

export const usePortalGlobalSearch = (options: UsePortalGlobalSearchOptions) => {
  const debouncedSearchTerm = debouncedRef(options.searchTerm, 250)
  const mappedFavouriteStores = PORTAL_SEARCH_FAVOURITE_MAPPINGS.map((mapping) => ({
    ...mapping,
    store: useFavouritesStore(mapping.kind),
  }))
  const normalizeFavouriteId = (id: string | number) => String(id).toLowerCase()
  const favouriteIdsByEntityType = computed(
    () =>
      new Map(
        mappedFavouriteStores.map(({ entityType, store }) => [
          entityType,
          new Set(store.favourites.map((favourite) => normalizeFavouriteId(favourite.id))),
        ]),
      ),
  )

  const isFavouriteResult = (result: PortalSearchResult) => {
    if (!result.sourceKey) return false

    return (
      favouriteIdsByEntityType.value
        .get(result.entityType as PortalSearchEntityType)
        ?.has(normalizeFavouriteId(result.sourceKey)) ?? false
    )
  }

  const favouriteFilters = computed(() =>
    mappedFavouriteStores
      .map(({ entityType, store }) => ({
        entityType,
        ids: store.favourites
          .map((favourite) => String(favourite.id))
          .map((id) => (entityType === 'document' ? id.toUpperCase() : id))
          .sort(),
      }))
      .filter(({ ids }) => ids.length > 0),
  )

  const favouriteCount = computed(() =>
    favouriteFilters.value.reduce((total, filter) => total + filter.ids.length, 0),
  )
  const hasFavourites = computed(() => favouriteCount.value > 0)
  const canSearch = computed(() => !options.favouritesOnly.value || hasFavourites.value)

  const criteria = computed<PortalSearchCriteria>(() => ({
    term: debouncedSearchTerm.value,
    language: options.language.value,
    selectedTypes: [...options.selectedTypes.value],
    favouritesOnly: options.favouritesOnly.value,
    favouriteFilters: favouriteFilters.value,
  }))
  const queryEnabled = computed(() => options.enabled.value && canSearch.value)

  const resultsQuery = useInfiniteQuery({
    queryKey: computed(() => ['portal-global-search', 'results', criteria.value]),
    enabled: queryEnabled,
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      useApiClient<PortalSearchResponse>('/hub/search-indices/portal-search', {
        method: 'POST',
        body: createPortalSearchResultsRequest(criteria.value, pageParam, PAGE_SIZE),
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((total, page) => total + page.value.length, 0)
      const total = allPages[0]?.['@odata.count']

      if (total !== undefined) return loaded < total ? loaded : undefined
      return lastPage.value.length === PAGE_SIZE ? loaded : undefined
    },
    ...SEARCH_CACHE_OPTIONS,
  })

  const facetsQuery = useQuery({
    queryKey: computed(() => ['portal-global-search', 'facets', criteria.value]),
    enabled: queryEnabled,
    queryFn: () =>
      useApiClient<PortalSearchFacetsResponse>('/hub/search-indices/portal-search', {
        method: 'POST',
        body: createPortalSearchFacetsRequest(criteria.value),
      }),
    ...SEARCH_CACHE_OPTIONS,
  })

  const results = computed(() => {
    if (!canSearch.value) return []

    return (
      resultsQuery.data.value?.pages.flatMap((page) =>
        page.value.map((item) => {
          const result = mapPortalSearchResult(item)
          return { ...result, isFavourite: isFavouriteResult(result) }
        }),
      ) ?? []
    )
  })
  const total = computed(() => {
    if (!canSearch.value) return 0
    return resultsQuery.data.value?.pages[0]?.['@odata.count'] ?? null
  })
  const facetCounts = computed<Record<string, number>>(() => {
    if (!canSearch.value) return {}

    const facets = facetsQuery.data.value?.['@search.facets']?.entityType ?? []
    return Object.fromEntries(facets.map((facet) => [facet.value, facet.count]))
  })
  const isLoading = computed(
    () => queryEnabled.value && resultsQuery.isPending.value && resultsQuery.isFetching.value,
  )
  const error = computed(() => resultsQuery.error.value)

  const retry = async () => {
    await Promise.all([resultsQuery.refetch(), facetsQuery.refetch()])
  }

  return {
    results,
    total,
    facetCounts,
    favouriteCount,
    hasFavourites,
    isLoading,
    isFetchingFacets: facetsQuery.isFetching,
    isFetchingNextPage: resultsQuery.isFetchingNextPage,
    error,
    hasNextPage: resultsQuery.hasNextPage,
    fetchNextPage: resultsQuery.fetchNextPage,
    retry,
  }
}
